import { useState, useEffect } from 'react';
import { galleriesData } from '../data/siteData';

export default function GalleryLightbox({ gallerySlug, title, defaultImages }) {
  const fallbackImages = defaultImages && defaultImages.length > 0 
    ? defaultImages 
    : (galleriesData[gallerySlug] || []);

  const [images, setImages] = useState(fallbackImages);
  const [galleryTitle, setGalleryTitle] = useState(title || '');
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!gallerySlug) return;
    
    // Ensure fallback is set if state was empty
    const initialFallback = defaultImages && defaultImages.length > 0 
      ? defaultImages 
      : (galleriesData[gallerySlug] || []);

    setLoading(true);
    fetch(`https://api.dev.auxo.dk/public/v2/content/galleri?gallery_slug=${encodeURIComponent(gallerySlug)}`)
      .then((res) => res.json())
      .then((data) => {
        const item = data?.items?.find(i => i.gallery_slug === gallerySlug || i.slug?._ === gallerySlug || i.slug === gallerySlug);
        if (item && item.images && Array.isArray(item.images) && item.images.length > 0) {
          setImages(item.images);
          if (item.title && !title) {
            setGalleryTitle(item.title);
          }
        } else if (initialFallback.length > 0) {
          setImages(initialFallback);
        }
      })
      .catch((err) => {
        console.error('Kunne ikke hente galleri fra CMS:', err);
        if (initialFallback.length > 0) {
          setImages(initialFallback);
        }
      })
      .finally(() => setLoading(false));
  }, [gallerySlug, title, defaultImages]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedIndex === null) return;
      if (e.key === 'Escape') setSelectedIndex(null);
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, images]);

  const handlePrev = () => {
    if (selectedIndex === null) return;
    setSelectedIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    if (selectedIndex === null) return;
    setSelectedIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  if (loading && images.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border border-[#B5A897] border-t-transparent"></div>
      </div>
    );
  }

  if (images.length === 0) return null;

  const optimizeUrl = (url, width = 800) => {
    if (!url) return '';
    if (url.includes('cdn-cgi/image/')) return url;
    return url.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`);
  };

  return (
    <div>
      {/* Lightbox Modal */}
      {selectedIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelectedIndex(null)}
        >
          {/* Close button */}
          <button
            onClick={() => setSelectedIndex(null)}
            className="absolute top-6 right-6 text-white/80 hover:text-white p-2 text-2xl z-50 transition-colors focus:outline-none"
            aria-label="Luk galleri"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>

          {/* Prev button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handlePrev();
            }}
            className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-black/40 hover:bg-black/70 p-3 sm:p-4 transition-all z-50 border border-white/20 focus:outline-none"
            aria-label="Forrige billede"
          >
            <i className="fa-solid fa-chevron-left text-xl" aria-hidden="true" />
          </button>

          {/* Main Image container */}
          <div
            className="relative max-w-5xl max-h-[85vh] flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={optimizeUrl(images[selectedIndex], 1600)}
              alt={`${galleryTitle || 'Galleri'} - Billede ${selectedIndex + 1}`}
              className="max-w-full max-h-[75vh] object-contain border border-white/10 shadow-2xl"
            />
            
            {/* Image counter */}
            <div className="mt-4 text-center text-xs tracking-widest text-stone-300 uppercase font-sans">
              <span>{selectedIndex + 1} / {images.length}</span>
              {galleryTitle && <span className="mx-2">• {galleryTitle}</span>}
            </div>
          </div>

          {/* Next button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-black/40 hover:bg-black/70 p-3 sm:p-4 transition-all z-50 border border-white/20 focus:outline-none"
            aria-label="Næste billede"
          >
            <i className="fa-solid fa-chevron-right text-xl" aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}
